// BUG-534 companion to PR #2425. Pure merger applied by
// `FunnelCacheInvalidationProvider` on every `funnel_event.updated` socket
// event. Replaces the previous EVICT behavior — eviction in busy funnels
// (e.g. Armonia en el Hogar, Birdie 9YV4C6l43) repeatedly emptied the cache
// during the 30s `recentlyEvictedFunnels` window, leaving SELLERs without
// `can_view_handle` (the "Medio" row hid). MERGE preserves scalars/stages/
// statuses from the wire payload and marks `users_stale: true` because the
// wire payload never carries `users[]`:
//
//   - websocket-server V1 fanout strips `users` from `funnel_event.updated`
//     (websocket/src/modules/application/event.service.ts:56-90)
//   - V2 transformer never sets `users`
//     (leads-service/src/leads/Infrastructure/Controllers/Responses/MdlFunnelTransformer.php:18-29)
//
// Consumer-side `Chat.hook.resetFunnelInfo` lazily refetches `users[]` from
// `ChatService.getWorkspacesInfo` when `users_stale === true`, preserving the
// original INT-945 invariant (newly-invited users appear in dropdown) without
// the destructive EVICT side effect.
//
// Plan: docs/plans/2026-05-26-002-fix-funnel-cache-evict-hides-medio-for-sellers-plan.md
// Origin: docs/plans/2026-05-25-001-fix-int-945-funneldata-cache-stale-on-invite-plan.md

import { tFunnelInfo } from 'src/components/Chat/Chat.types';
import {
  eFunnelStatusName,
  eLeadStatus,
} from 'src/providers/SocketsProvider/Sockets.enums';
import { Funnel } from 'src/providers/SocketsProvider/Sockets.types';

// Boundary coercion: only `boolean` is accepted. Schema-drift values such as
// `1`, `"true"`, `null` would otherwise tunnel through `??` and surface in
// the `ShowTotalAccess` gate as a non-boolean falsy/truthy value. The merger
// rejects them here so the consumer can rely on `boolean` typing.
function pickBoolean(
  fromPayload: unknown,
  fromExisting: boolean,
): boolean {
  return typeof fromPayload === 'boolean' ? fromPayload : fromExisting;
}

// `eFunnelStatusName` and `eLeadStatus` are nominally distinct enums with
// identical member sets (Sockets.enums.ts:112-126). TypeScript refuses the
// direct `name as eLeadStatus` cast, and `as unknown as eLeadStatus` is the
// pattern banned by docs/solutions/logic-errors/unsafe-cast-masks-backend-shape-drift.md.
// Runtime validator: coerce via plain string lookup against `eLeadStatus`
// values. If the backend ever sends a name outside the shared set (the only
// real drift risk this guards against), fall back to NEUTRAL rather than
// smuggling an invalid enum into downstream consumers.
function statusNameAsLeadStatus(name: eFunnelStatusName): eLeadStatus {
  const asString: string = name;
  const matched = Object.values(eLeadStatus).find((v) => v === asString);
  return matched ?? eLeadStatus.NEUTRAL;
}

// Pure function — never throws, no I/O, no side effects. Idempotent under
// repeated calls with the same payload (R7).
export function mergeFunnelFromEvent(
  payload: Funnel,
  existing: tFunnelInfo,
): tFunnelInfo {
  // Booleans: boundary coercion.
  const canViewHandle = pickBoolean(payload.can_view_handle, existing.can_view_handle);
  const isDefault = pickBoolean(payload.is_default, existing.is_default);
  const isPublic = pickBoolean(payload.is_public, existing.is_public);

  // Strings: `||` for empty-string defense (BUG-460 convention — partial
  // payloads can send "" for fields the backend hasn't populated yet).
  const name = payload.name || existing.name;
  const description = payload.description || existing.description;
  const emoji = payload.emoji || existing.emoji;

  // Arrays: empty-array preservation. An empty `stages` / `statuses` on the
  // wire is treated as "not authoritative" — keep the cached version. This
  // mirrors the BUG-460 reducer in Pipe.functions.ts.
  const stages =
    Array.isArray(payload.stages) && payload.stages.length > 0
      ? payload.stages.map((s) => ({ id: s.uuid, name: s.name }))
      : existing.stages;

  const statuses =
    Array.isArray(payload.statuses) && payload.statuses.length > 0
      ? payload.statuses.map((s) => ({
          id: s.uuid,
          name: statusNameAsLeadStatus(s.name),
        }))
      : existing.statuses;

  return {
    uuid: existing.uuid,
    name,
    description,
    emoji,
    is_default: isDefault,
    is_public: isPublic,
    can_view_handle: canViewHandle,
    // Creation-time attribute, never on the wire payload.
    from_industry_template: existing.from_industry_template,
    stages,
    statuses,
    // NEVER read `payload.users` — it is contractually undefined per the
    // websocket-server V1 strip and V2 transformer never setting it. Even if
    // a future backend change populates it, the consumer-side refetch path
    // is authoritative for `users[]`. Preserve existing and mark stale.
    users: existing.users,
    users_stale: true,
    // Monotonic bump for the race-safe lazy-refetch clear in `Chat.hook.ts`.
    cache_version: (existing.cache_version ?? 0) + 1,
  };
}

// Used by `FunnelCache.provider.tsx` to short-circuit the SYNC dispatch when
// the merge changed nothing observable. Compares only the merger's output
// fields — `users` is by reference (the merger always preserves
// `existing.users` so this is the same reference unless something upstream
// replaced it).
//
// NOTE: `stages` and `statuses` always produce a NEW array reference when
// the payload's normalized arrays are non-empty (the merger calls `.map()`).
// So this short-circuit only fires on the "preserve existing arrays" branch
// (payload omitted stages/statuses, or sent empty arrays). That is by design
// — content-equality would add complexity for marginal write savings, and
// the structural fix is BE emission dampening (deferred follow-up).
export function shallowEqualOnMergedFields(
  a: tFunnelInfo,
  b: tFunnelInfo,
): boolean {
  return (
    a.uuid === b.uuid &&
    a.name === b.name &&
    a.description === b.description &&
    a.emoji === b.emoji &&
    a.is_default === b.is_default &&
    a.is_public === b.is_public &&
    a.can_view_handle === b.can_view_handle &&
    a.stages === b.stages &&
    a.statuses === b.statuses &&
    a.users_stale === b.users_stale
  );
}
